import React from 'react';
import { Helmet } from 'react-helmet';
import data from './marketing_data.json';
import notes from './field_notes_index.json';
import { SiteHeader, SiteFooter, StoreBadges, money, absolute, youngOffer, organizationNode, breadcrumbNode } from './marketing_chrome';

// This component is rendered to static HTML at build time (scripts/build-public-pages.cjs)
// and hydrated in the browser, so it must stay free of browser-only APIs and webpack-only imports.

export { money };
const savings = data.competitorAnnualPrice - data.annualPrice;

const homeFaq = [
  ['Is BiteTracker free?', `BiteTracker offers a trial so you can explore the app. Continued access is a paid subscription: ${money(data.annualPrice)} per year, or ${money(data.monthlyPrice)} billed monthly, with every feature included.`],
  ['Which devices does BiteTracker work on?', 'iPhone, Android and any modern web browser. One account keeps your diary, goals and recipes in sync across all of them.'],
  ['How accurate is AI food logging?', 'AI estimates are a starting point. Review the food identity and portion size before saving, and scan the barcode on packaged foods when you need exact numbers.'],
  ['Can I import my MyFitnessPal history?', 'There is no automatic import. Most people start fresh: set calorie and macro goals, log a typical day, then save the meals they repeat so future logging takes seconds.'],
  ['Is there a discount for young people?', `Yes. If you are ${data.youngMaximumAge} or under, the annual plan is ${money(youngOffer)} for the first year instead of ${money(data.annualPrice)}. Claim it in your account before subscribing, and check the renewal price at checkout.`]
];

const pricingFaq = [
  ['What does the subscription include?', 'Calorie and macro tracking, AI food logging, barcode scanning, meal planning, saved recipes and progress tracking. AI nutrition estimates should be checked for food identity and portion size.'],
  ['Do I need a promo code?', `No. ${money(data.annualPrice)} a year and ${money(data.monthlyPrice)} a month are the prices for everyone, with nothing to claim. The only separate offer is the young-user price for people aged ${data.youngMaximumAge} and under.`],
  ['Who gets the young-user discount?', `Users aged ${data.youngMaximumAge} and under can claim the young-user offer through their account. It applies to monthly and annual plans, not lifetime access. The annual offer is ${money(youngOffer)} for the first year; confirm eligibility and renewal terms before paying.`],
  [`Is ${money(data.annualPrice / 12)} a monthly subscription?`, `No. It is the monthly equivalent of ${money(data.annualPrice)} paid annually. The monthly subscription is ${money(data.monthlyPrice)}, billed each month.`],
  ['What happens when the young-user offer ends?', `It lasts 12 months. Your checkout and subscription details show the renewal price, so check them before paying, and do not assume the young-user rate continues beyond the first year.`],
  ['Is BiteTracker completely free?', 'BiteTracker offers a trial so you can explore the app. Continued subscription access is paid. Review the trial and billing terms in your account before subscribing.']
];

const loggingMethods = [
  ['Describe it', 'Type “two eggs, toast and a latte”. AI estimates portions and nutrition, and you confirm before it lands in your diary.'],
  ['Snap a photo', 'Photograph the plate. Food recognition suggests the items and amounts for you to review.'],
  ['Scan a barcode', 'Packaged foods pull their nutrition label in one scan, so the numbers match the box.'],
  ['Search or repeat', 'Search generic and branded foods, or re-add the meals you eat every week in a tap.']
];

const features = [
  ['Calories & macros', 'Daily targets for calories, protein, carbs and fat, with a running total that updates as you log.'],
  ['Weight, steps & water', 'Log weight and hydration, sync steps from Apple Health or Health Connect, and follow the trend over weeks.'],
  ['Meal calendar', 'Plan the week by placing meals and recipes into breakfast, lunch, dinner and snacks before the week starts.'],
  ['Recipes', 'Save your own recipes, import one from a link, or cook from the BiteTracker recipe book with macros attached.'],
  ['AI nutrition coach', 'Ask about your day, get meal ideas that fit what is left in your budget, and plan around your goals.'],
  ['Grocery ordering', 'Turn a planned week into an Instacart order so the ingredients arrive before you need them.']
];

const meals = [
  ['Breakfast', 'Greek yogurt, berries & granola', 380, 'Photo'],
  ['Lunch', 'Chicken burrito bowl', 640, 'Described'],
  ['Snack', 'Protein bar', 210, 'Scanned'],
  ['Dinner', 'Salmon, rice & greens', 620, 'Planned']
];
const macros = [['Protein', 96, 150, 'protein'], ['Carbs', 118, 210, 'carbs'], ['Fat', 41, 70, 'fat']];
const loggedCalories = meals.filter(meal => meal[3] !== 'Planned').reduce((total, meal) => total + meal[2], 0);
const calorieGoal = 2100;
const ringLength = 2 * Math.PI * 52;

// The phone is a physical object on the paper: its layers sit at different depths and,
// on desktop, it turns a few degrees toward the cursor. The static markup already shows a
// resting tilt, so the prerendered page looks finished before (or without) hydration.
function useDeviceTilt(ref) {
  React.useEffect(() => {
    const el = ref.current;
    const fine = window.matchMedia('(pointer: fine)').matches;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!el || !fine || reduced) return undefined;
    let frame = 0, x = 0, y = 0;
    const apply = () => {
      frame = 0;
      el.style.setProperty('--bt-tilt-y', `${(x * 8 - 12).toFixed(2)}deg`);
      el.style.setProperty('--bt-tilt-x', `${(5 - y * 6).toFixed(2)}deg`);
    };
    const onMove = e => {
      const r = el.getBoundingClientRect();
      x = Math.max(-1, Math.min(1, (e.clientX - (r.left + r.width / 2)) / (window.innerWidth / 2)));
      y = Math.max(-1, Math.min(1, (e.clientY - (r.top + r.height / 2)) / (window.innerHeight / 2)));
      if (!frame) frame = requestAnimationFrame(apply);
    };
    el.classList.add('bt-device--live');
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => { window.removeEventListener('pointermove', onMove); if (frame) cancelAnimationFrame(frame); };
  }, [ref]);
}

function DiaryPreview() {
  const ref = React.useRef(null);
  useDeviceTilt(ref);
  const ringFill = ringLength * loggedCalories / calorieGoal;
  return <figure className="bt-device" ref={ref} aria-labelledby="bt-device-caption">
    <div className="bt-device-body" aria-hidden="true">
    <span className="bt-device-shadow" />
    <div className="bt-screen">
      <div className="bt-screen-top"><span>Today</span><strong>3 meals logged</strong></div>
      <div className="bt-summary">
        <div className="bt-ring">
          <svg viewBox="0 0 120 120" width="120" height="120" focusable="false">
            <circle cx="60" cy="60" r="52" className="bt-ring-track" />
            <circle cx="60" cy="60" r="52" className="bt-ring-progress" style={{ strokeDasharray: `${ringFill.toFixed(1)} ${ringLength.toFixed(1)}`, '--bt-ring-fill': ringFill.toFixed(1) }} />
          </svg>
          <div className="bt-ring-text"><strong>{loggedCalories.toLocaleString('en-US')}</strong><span>of {calorieGoal.toLocaleString('en-US')} cal</span></div>
        </div>
        <ul className="bt-macros">
          {macros.map(([name, eaten, goal, tone]) => <li key={name} className={`bt-macro bt-macro-${tone}`}>
            <span>{name}</span><i><b style={{ width: `${Math.round(eaten / goal * 100)}%` }} /></i><em>{eaten} / {goal} g</em>
          </li>)}
        </ul>
      </div>
      <ul className="bt-meals">
        {meals.map(([slot, food, calories, method]) => <li key={slot} className={method === 'Planned' ? 'bt-meal-planned' : undefined}>
          <span className="bt-meal-slot">{slot}</span><span className="bt-meal-food">{food}</span><span className="bt-meal-cal">{calories} cal</span><span className="bt-chip">{method}</span>
        </li>)}
      </ul>
      <div className="bt-screen-cta">＋ Add a meal</div>
    </div>
    <div className="bt-logged">
      <span className="bt-logged-mark">✓</span>
      <span className="bt-logged-text"><strong>{(calorieGoal - loggedCalories).toLocaleString('en-US')} cal left today</strong><em>Tonight’s salmon fits your plan</em></span>
    </div>
    </div>
    <figcaption id="bt-device-caption">An illustration of a BiteTracker day: one calorie ring, three macros and four meals.</figcaption>
  </figure>;
}

function Faq({ id, eyebrow, title, items }) {
  return <section className="bt-faq" aria-labelledby={id}>
    <div className="bt-section-heading"><span className="bt-eyebrow">{eyebrow}</span><h2 id={id}>{title}</h2></div>
    <div className="bt-faq-list">
      {items.map(([question, answer]) => <article key={question}><h3>{question}</h3><p>{answer}</p></article>)}
    </div>
  </section>;
}

export function PriceOptions() {
  return <section className="bt-prices" aria-labelledby="bt-prices-title">
    <div className="bt-section-heading"><span className="bt-eyebrow">Room in your budget</span><h2 id="bt-prices-title">A little less on tracking.<br />A little more on living.</h2></div>
    <div className="bt-price-options">
      <article>
        <h3>Annual plan</h3>
        <p className="bt-price">{money(data.annualPrice)}<span> / year</span></p>
        <p className="bt-price-note">{money(data.annualPrice / 12)}/month equivalent, billed annually.</p>
        <p>Every feature included. Prefer to pay monthly? {money(data.monthlyPrice)} a month.</p>
      </article>
      <article>
        <h3>Ages {data.youngMaximumAge} and under</h3>
        <p className="bt-price">{money(youngOffer)}<span> / first year</span></p>
        <p className="bt-price-note">{money(youngOffer / 12)}/month equivalent, billed annually.</p>
        <p>For eligible young users. Claim the young-user offer in your account; it lasts 12 months.</p>
      </article>
    </div>
    <p className="bt-terms">Prices in USD. The young-user offer must be claimed in your account before payment. Check the total and renewal price at checkout.</p>
    <div className="bt-actions"><a className="bt-button" href="/signup">Create an account</a><a className="bt-link" href="/pricing">Full pricing &amp; offer terms</a></div>
  </section>;
}

function Home() {
  return <>
    <section className="bt-hero">
      <div className="bt-hero-copy">
        <span className="bt-eyebrow">Calorie &amp; macro tracker · iPhone, Android and web</span>
        <h1>Track calories.<br /><em>Keep your budget.</em></h1>
        <p className="bt-lead">BiteTracker is an affordable calorie counter with AI food logging, barcode scanning and a weekly meal planner. Log a meal in seconds, then watch calories, protein, carbs and fat add up through the day.</p>
        <p className="bt-offer">{money(data.annualPrice)}/year, every feature included. Ages {data.youngMaximumAge} and under: {money(youngOffer)} for the first year.</p>
        <p className="bt-small">Annual billing, or {money(data.monthlyPrice)} billed monthly. Young-user eligibility applies.</p>
        <div className="bt-actions"><a className="bt-button" href="/signup">Start tracking</a><a className="bt-link" href="/pricing">See pricing</a></div>
        <StoreBadges className="bt-stores-hero" />
      </div>
      <DiaryPreview />
    </section>

    <section className="bt-logging" aria-labelledby="bt-logging-title">
      <div className="bt-section-heading"><span className="bt-eyebrow">Food logging without the chore</span><h2 id="bt-logging-title">Log it the way that fits the moment.</h2></div>
      <ol className="bt-steps">
        {loggingMethods.map(([title, text], index) => <li key={title}><span className="bt-step-number">0{index + 1}</span><h3>{title}</h3><p>{text}</p></li>)}
      </ol>
    </section>

    <section className="bt-features" id="features" aria-labelledby="bt-features-title">
      <div className="bt-section-heading"><span className="bt-eyebrow">Included in every plan</span><h2 id="bt-features-title">Everything you need to hit your goals.</h2></div>
      <div className="bt-feature-grid">
        {features.map(([title, text]) => <article key={title}><h3>{title}</h3><p>{text}</p></article>)}
      </div>
    </section>

    <section className="bt-spotlight" aria-labelledby="bt-spotlight-title">
      <div className="bt-spotlight-copy">
        <span className="bt-eyebrow">iPhone Live Activities</span>
        <h2 id="bt-spotlight-title">Your day, on your lock screen.</h2>
        <p>On iPhone, BiteTracker keeps calories and protein on the lock screen and adds a meal with one tap. No unlocking, no hunting for the app between meetings.</p>
        <p className="bt-small">Live Activities are an iOS feature. Android and web keep the same diary without the lock-screen card.</p>
      </div>
      <img src="/marketing/live-activity.webp" width="900" height="419" alt="iPhone lock screen showing the BiteTracker Live Activity with calories, protein and one-tap meal buttons" loading="lazy" />
    </section>

    <PriceOptions />

    <section className="bt-compare-teaser" aria-labelledby="bt-compare-title">
      <div>
        <span className="bt-eyebrow">Thinking about switching?</span>
        <h2 id="bt-compare-title">{money(savings)} a year less than MyFitnessPal Premium.</h2>
        <p>At standard annual prices, BiteTracker costs {money(data.annualPrice)} against {money(data.competitorAnnualPrice)} for MyFitnessPal Premium, with AI logging, barcode scanning and meal planning included. Compare the tools you actually use before you decide.</p>
        <p className="bt-small">US web prices checked {data.comparisonChecked}. Regional prices and app-store offers may differ.</p>
      </div>
      <a className="bt-button bt-button-secondary" href="/compare/myfitnesspal">BiteTracker vs MyFitnessPal</a>
    </section>

    <Faq id="bt-home-faq" eyebrow="Quick answers" title="The details, up front." items={homeFaq} />

    <section className="bt-recipes-teaser" aria-labelledby="bt-recipes-title">
      <div>
        <span className="bt-eyebrow">BiteTracker recipe book</span>
        <h2 id="bt-recipes-title">Recipes with the numbers already counted.</h2>
        <p>Browse the recipe book for breakfasts, lunches, dinners and snacks with calories, protein, carbs and fat per serving. Cook one, then log it in a tap.</p>
        <p className="bt-small">Popular: <a href="/recipe-book/collections/cheap-high-protein">cheap high protein meals</a>, <a href="/recipe-book/collections/breakfast">breakfast</a> and <a href="/recipe-book/collections/dinner">dinner</a>.</p>
      </div>
      <a className="bt-button bt-button-secondary" href="/recipe-book">Browse healthy recipes</a>
    </section>

    {notes.length > 0 && <section className="bt-notes" aria-labelledby="bt-notes-title">
      <div className="bt-section-heading"><span className="bt-eyebrow">Field Notes</span><h2 id="bt-notes-title">Practical guides from the BiteTracker team.</h2></div>
      <div className="bt-note-grid">
        {notes.slice(0, 3).map(note => <article key={note.slug}>
          <span className="bt-note-meta">{note.category} · {note.readTime}</span>
          <h3><a href={`/field-notes/${note.slug}`}>{note.title}</a></h3>
          <p>{note.excerpt}</p>
        </article>)}
      </div>
      <a className="bt-link" href="/field-notes">All food tracking guides</a>
    </section>}
  </>;
}

function Pricing() {
  return <>
    <header className="bt-page-heading">
      <span className="bt-eyebrow">BiteTracker pricing</span>
      <h1>Good habits.<br /><em>Down-to-earth pricing.</em></h1>
      <p className="bt-lead">One plan with every feature. Pay annually for the lowest yearly cost, or monthly if you prefer.</p>
    </header>
    <PriceOptions />
    <Faq id="bt-pricing-faq" eyebrow="Billing and offers" title="The details, up front." items={pricingFaq} />
    <section className="bt-closing"><a className="bt-link" href="/compare/myfitnesspal">Compare with MyFitnessPal Premium</a></section>
  </>;
}

function Comparison() {
  return <>
    <header className="bt-page-heading">
      <span className="bt-eyebrow">BiteTracker vs MyFitnessPal</span>
      <h1>A familiar goal.<br /><em>A smaller annual bill.</em></h1>
      <p className="bt-lead">Looking for an affordable MyFitnessPal alternative? Compare the cost of paid tracking and the tools you actually use.</p>
      <p className="bt-small">Published by BiteTracker. US web prices checked {data.comparisonChecked}. Regional prices, promotions and app-store offers may differ.</p>
    </header>
    <section className="bt-comparison" aria-labelledby="bt-comparison-title">
      <h2 id="bt-comparison-title">Start with the standard price.</h2>
      <div className="bt-table-wrap">
        <table>
          <caption>Annual subscriptions in USD; the young-user offer is listed separately</caption>
          <thead><tr><th scope="col">Plan</th><th scope="col">Annual cost</th><th scope="col">Conditions</th></tr></thead>
          <tbody>
            <tr><th scope="row">BiteTracker standard</th><td>{money(data.annualPrice)}</td><td>Annual billing</td></tr>
            <tr><th scope="row">BiteTracker young-user offer</th><td>{money(youngOffer)}</td><td>Ages {data.youngMaximumAge} and under; first year</td></tr>
            <tr><th scope="row">MyFitnessPal Premium</th><td>{money(data.competitorAnnualPrice)}</td><td>Published standard annual web price</td></tr>
          </tbody>
        </table>
      </div>
      <p>At standard annual prices, BiteTracker costs {money(savings)} less per year than MyFitnessPal Premium. This compares paid plans, not MyFitnessPal’s free tier or Premium+.</p>
      <p><a className="bt-link" href={data.competitorSource} rel="nofollow noopener">Source: MyFitnessPal’s official Premium pricing and features</a></p>
    </section>
    <section className="bt-faq" aria-labelledby="bt-fit-title">
      <div className="bt-section-heading"><span className="bt-eyebrow">Which fits your routine?</span><h2 id="bt-fit-title">Try a typical day before switching.</h2></div>
      <div className="bt-faq-list">
        <article><h3>Consider BiteTracker if…</h3><p>You want calorie and macro tracking alongside AI food logging, barcode scanning, a meal calendar and saved recipes at a lower standard annual price. Try logging your usual meals to see whether the workflow fits.</p></article>
        <article><h3>Consider MyFitnessPal if…</h3><p>You are happy with its free food and exercise tracking or already rely on its workflow. Its paid Premium plan includes barcode scanning, voice logging, custom macro tracking and an ad-free experience. Switching is only useful if the new app works better for you.</p></article>
        <article>
          <h3>A one-day test</h3>
          <ol>
            <li>Set up your calorie and macro goals in BiteTracker.</li>
            <li>Log your usual breakfast, one packaged food and a homemade meal.</li>
            <li>Check portions, nutrition estimates and any device integrations you rely on.</li>
            <li>Review the billing terms before subscribing.</li>
          </ol>
          <p>This guide does not promise automatic import of your MyFitnessPal history.</p>
        </article>
      </div>
      <div className="bt-actions"><a className="bt-button" href="/pricing">Explore BiteTracker pricing</a><a className="bt-link" href="/signup">Create an account</a></div>
    </section>
  </>;
}

function structuredData(path, page) {
  const organization = `${data.origin}/#organization`;
  const faq = path === '/' ? homeFaq : path === '/pricing' ? pricingFaq : null;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      organizationNode(),
      { '@type': 'WebSite', '@id': `${data.origin}/#website`, url: data.origin, name: 'BiteTracker', publisher: { '@id': organization } },
      {
        '@type': 'SoftwareApplication', '@id': `${data.origin}/#app`, name: 'BiteTracker: Calories & Macros', applicationCategory: 'HealthApplication',
        operatingSystem: 'iOS, Android, Web', url: data.origin, installUrl: [data.appStoreUrl, data.playStoreUrl], image: absolute(data.ogImage),
        description: data.pages['/'].description, publisher: { '@id': organization },
        offers: [
          { '@type': 'Offer', name: 'Annual subscription', price: data.annualPrice.toFixed(2), priceCurrency: 'USD', url: absolute('/pricing'), description: 'Standard annual price.' },
          { '@type': 'Offer', name: 'Monthly subscription', price: data.monthlyPrice.toFixed(2), priceCurrency: 'USD', url: absolute('/pricing'), description: 'Standard monthly price.' }
        ]
      },
      { '@type': 'WebPage', '@id': `${absolute(path)}#webpage`, url: absolute(path), name: page.title, description: page.description, isPartOf: { '@id': `${data.origin}/#website` }, about: { '@id': `${data.origin}/#app` }, primaryImageOfPage: absolute(data.ogImage) },
      path !== '/' && breadcrumbNode([['Home', '/'], [page.breadcrumb, path]]),
      faq && { '@type': 'FAQPage', mainEntity: faq.map(([question, answer]) => ({ '@type': 'Question', name: question, acceptedAnswer: { '@type': 'Answer', text: answer } })) }
    ].filter(Boolean)
  };
}

export default function MarketingPage({ path = '/' }) {
  const page = data.pages[path] || data.pages['/'];
  const pageUrl = absolute(path);
  const image = absolute(data.ogImage);
  return <div className="bt-marketing">
    <Helmet>
      <title>{page.title}</title>
      <meta name="description" content={page.description} />
      <meta name="robots" content="index,follow,max-image-preview:large" />
      <link rel="canonical" href={pageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="BiteTracker" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:title" content={page.title} />
      <meta property="og:description" content={page.description} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="BiteTracker: track calories, keep your budget." />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={page.title} />
      <meta name="twitter:description" content={page.description} />
      <meta name="twitter:image" content={image} />
      <script type="application/ld+json">{JSON.stringify(structuredData(path, page))}</script>
    </Helmet>
    <a className="bt-skip" href="#bt-main">Skip to content</a>
    <SiteHeader path={path} />
    <main id="bt-main" className="bt-wrap">{path === '/pricing' ? <Pricing /> : path === '/compare/myfitnesspal' ? <Comparison /> : <Home />}</main>
    <SiteFooter />
  </div>;
}
